<template>
	<main class="auth_theme_container mcp_connect">
		<div class="mcp_card">
			<div class="mcp_handshake" aria-hidden="true">
				<span class="mcp_handshake-brand"><Logo /></span>
				<span class="mcp_handshake-link">
					<span></span><span></span><span></span>
				</span>
				<span class="mcp_handshake-phi">φ</span>
			</div>

			<h1 class="step_title">Conecta Phi a tu cuenta</h1>
			<p class="step_subtitle">
				Inicia sesión para autorizar el acceso de tu asistente de IA a
				los datos de CFOremoto.
			</p>

			<p v-if="!loginChallenge" class="mcp_error">
				Falta el parámetro de autorización. Volvé a iniciar la conexión
				desde tu cliente de IA.
			</p>

			<div v-else-if="autoContinuing" class="mcp_session" role="status">
				<span class="mcp_spinner" aria-hidden="true"></span>
				<span>
					Continuando como <strong>{{ sessionEmail }}</strong
					>…
				</span>
			</div>

			<form v-else @submit.prevent="submit">
				<div class="field">
					<label class="field_label" for="mcp_email">Email</label>
					<input id="mcp_email" v-model="email" class="field_input"
						type="email" placeholder="correo@ejemplo.cl"
						autocomplete="username" required :disabled="loading" />
				</div>

				<div class="field">
					<label class="field_label" for="mcp_password">Contraseña</label>
					<input id="mcp_password" v-model="password" class="field_input"
						type="password" placeholder="Tu contraseña de CFOremoto"
						autocomplete="current-password" required :disabled="loading" />
				</div>

				<button class="continue_button"
					:disabled="loading || !email || !password">
					{{ loading ? "Verificando…" : "Continuar" }}
				</button>

				<p v-if="error" class="mcp_error">{{ error }}</p>
			</form>
		</div>

		<p class="mcp_foot">
			¿Problemas para conectar?
			<a href="mailto:soporte@cforemoto.com">soporte@cforemoto.com</a>
		</p>
	</main>
</template>

<script>
// Página STANDALONE (sin shell de la app) del dance OAuth de la Puerta 2
// (plan MCP fase 6b): Hydra redirige acá con ?login_challenge=…, la password
// se verifica en Django (POST /mcp/login/accept) y se vuelve a Hydra vía el
// redirect_to. La password nunca toca el MCP ni Hydra.
//
// Si el browser YA tiene sesión viva de la app, se salta la password: el JWT
// es la prueba de identidad (accept-session) y el usuario cae directo al
// consent — que sigue siendo el paso explícito. Sesión vencida se limpia
// ANTES de llamar (el interceptor de api.js mataría el request y redirigiría
// a "/", perdiendo el login_challenge) y se cae al form.
//
// Presentación: vive dentro de .auth_theme_container (tokens y controles del
// theme de auth) + .mcp_connect (_mcp_connect.scss) — sin estilos propios.
import Logo from "@/components/svg/Logo.vue";
import { acceptMcpLogin, acceptMcpLoginWithSession } from "@/api/mcpConsent";
import { clearLocalStorage, getSession, hasLiveSession } from "@/utils/auth";
import { readOauthChallenge } from "@/utils/oauthChallenge";

export default {
	name: "McpAuthorize",
	components: { Logo },
	data() {
		return {
			email: "",
			password: "",
			loading: false,
			error: "",
			autoContinuing: false,
			sessionEmail: "",
		};
	},
	computed: {
		loginChallenge() {
			return readOauthChallenge(this.$route, "login_challenge");
		},
	},
	async created() {
		if (!this.loginChallenge) {
			return;
		}
		const session = getSession();
		if (!session) {
			return;
		}
		if (!hasLiveSession()) {
			clearLocalStorage();
			return;
		}
		this.autoContinuing = true;
		this.sessionEmail = session.user?.email || "";
		try {
			const { data } = await acceptMcpLoginWithSession(this.loginChallenge);
			this.redirect(data.redirect_to);
		} catch (err) {
			// Cualquier fallo (sesión rechazada, flag apagado, Hydra caído)
			// degrada al form de password con el mensaje correspondiente.
			this.autoContinuing = false;
			const status = err?.response?.status;
			if (status === 403) {
				this.error = "Tu sesión no tiene habilitado el acceso a Phi.";
			} else if (status === 400) {
				this.error = "La autorización venció. Volvé a iniciar la conexión desde tu cliente de IA.";
			}
		}
	},
	methods: {
		redirect(url) {
			// Método propio para poder espiarlo en los specs.
			window.location.assign(url);
		},
		async submit() {
			this.loading = true;
			this.error = "";
			try {
				const { data } = await acceptMcpLogin(
					this.email.trim(), this.password, this.loginChallenge,
				);
				this.redirect(data.redirect_to);
			} catch (err) {
				const status = err?.response?.status;
				if (status === 429) {
					this.error = "Demasiados intentos. Esperá unos minutos y probá de nuevo.";
				} else if (status === 403) {
					this.error = "Credenciales inválidas o acceso no habilitado.";
				} else if (status === 400) {
					this.error = "La autorización venció. Volvé a iniciar la conexión desde tu cliente de IA.";
				} else {
					this.error = "No pudimos procesar la solicitud. Intentá de nuevo.";
				}
				this.loading = false;
			}
		},
	},
};
</script>
